import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import ProductVariantPicker from "./ProductVariantPicker";
import { LOW_STOCK_THRESHOLD, useLowStockThreshold } from "./StockBadge";

/**
 * InlineCartControl — Amazon / Flipkart–style inline cart control that
 * sits directly on a product listing card. Renders "ADD" when the item
 * isn't in the cart; swaps to a −/qty/+ pill once added.
 *
 * The parent supplies ``currentQty`` (looked up from a page-level cart
 * cache) so multiple cards can share one ``GET /cart`` fetch. Every
 * mutation dispatches ``cart:update`` so the sticky cart bar + parent
 * cache both refresh.
 *
 * Behavioural parity with ``ProductCard.jsx`` inside the shop menu:
 *   • Multi-variant products open the ``ProductVariantPicker`` before
 *     the FIRST add. Subsequent inc/dec on the same card operate on
 *     the picked variant, keeping the marketplace experience simple.
 *   • Sold-out items render a "SOLD OUT" pill instead.
 *   • Services (``kind === "SERVICE"``) render nothing — booking flows
 *     stay on the shop detail page.
 *   • Cross-shop conflicts (409 DIFFERENT_RESTAURANT) prompt to clear
 *     and re-add.
 *   • Unauthenticated tap redirects to /login with a return path.
 *
 * Tap targets stop propagation so a card wrapped in an onClick / Link
 * doesn't double-trigger navigation.
 */
// ``compact`` (optional, off by default) narrows the control for tight product tiles. ``variantId`` (optional) is for a
// caller that has already chosen the variant (the Blinkit product page's option chips): adds / ± then apply to that
// variant without opening the picker. Callers passing neither behave exactly as before.
export default function InlineCartControl({ product, currentQty = 0, size = "sm", fullWidth = false, compact = false, variantId = undefined }) {
    const { user } = useAuth();
    const nav = useNavigate();
    const [busy, setBusy] = useState(false);
    const [qty, setQty] = useState(currentQty);
    const [pickedVariantId, setPickedVariantId] = useState(variantId ?? null);
    const [variantPickerOpen, setVariantPickerOpen] = useState(false);

    // Sync from parent cache — but ONLY when the ``currentQty`` prop
    // actually changes. Previously this depended on ``busy`` too,
    // which caused a race after removal: our own PATCH would flip
    // ``busy`` off before the parent finished refetching /cart, so
    // the effect fired with a stale ``currentQty=1`` and clobbered
    // our optimistic ``qty=0`` — the "− 1 +" pill reappeared instead
    // of the ADD button on qty-1 removal. Guarding on prop change
    // only (via a ref) fixes it.
    const lastPropQty = useRef(currentQty);
    useEffect(() => {
        if (currentQty !== lastPropQty.current) {
            lastPropQty.current = currentQty;
            setQty(currentQty);
        }
    }, [currentQty]);
    useEffect(() => {
        if (variantId !== undefined) setPickedVariantId(variantId);
    }, [variantId]);

    if (product?.kind === "SERVICE") return null;

    const trackStock = product?.track_stock !== false;
    const isSoldOut = trackStock && (
        product?.status === "OUT_OF_STOCK"
        || (product?.stock != null && Number(product.stock) <= 0)
    );
    const remaining = trackStock && product?.stock != null
        ? Math.max(0, Number(product.stock) - qty)
        : Infinity;
    const cantAddMore = remaining <= 0;
    const enabledVariants = (product?.variants || []).filter((v) => v?.enabled !== false);
    const hasVariantChoice = enabledVariants.length >= 2;

    // Shared box — every state (ADD / qty pill / SOLD OUT) reuses these
    // classes so the card height & width stay pixel-identical across
    // add-cart transitions. Only the interior content changes.
    const boxHeight = size === "sm" ? "h-9" : "h-11";
    const textSize = size === "sm" ? "text-xs" : "text-sm";
    const iconSize = size === "sm" ? 14 : 16;
    const widthCls = fullWidth ? "w-full" : (compact ? "min-w-[68px]" : "min-w-[92px]");
    const stepPad = compact ? "px-1.5" : "px-3";
    const boxBase = `${boxHeight} ${widthCls} ${textSize} rounded-lg border-2 font-bold tracking-wider transition select-none inline-flex items-center justify-center overflow-hidden`;

    if (isSoldOut) {
        return (
            <span
                data-testid={`inline-sold-out-${product.id}`}
                className={`${boxBase} bg-muted text-muted-foreground border-border`}
            >
                SOLD OUT
            </span>
        );
    }

    const post = async (delta) => {
        const newQty = Math.max(0, qty + delta);
        if (delta > 0 && cantAddMore) return;
        if (!user) {
            nav("/login", { state: { from: window.location.pathname || "/" } });
            return;
        }
        // First add of a multi-variant product → open picker.
        if (delta > 0 && qty === 0 && hasVariantChoice && !pickedVariantId) {
            setVariantPickerOpen(true);
            return;
        }
        const sid = product.restaurant_id || product.shop_id;
        if (!sid) {
            toast.error("This product is not available for ordering");
            return;
        }
        setBusy(true);
        try {
            if (newQty === 0) {
                // Removal — PATCH /cart/items/{pid}?quantity=0 drops it.
                // Rolls back the optimistic ``qty`` if the request fails
                // so a network hiccup doesn't leave the card showing
                // ADD while the cart still has the item.
                try {
                    await api.patch(
                        `/cart/items/${product.id}?quantity=0${pickedVariantId ? `&variant_id=${pickedVariantId}` : ""}`,
                    );
                    setQty(0);
                    setPickedVariantId(variantId ?? null);
                    window.dispatchEvent(new Event("cart:update"));
                } catch (e) {
                    toast.error(e?.response?.data?.detail || "Could not remove item");
                    // Force a cart:update so the parent refetches truth.
                    window.dispatchEvent(new Event("cart:update"));
                }
                return;
            }
            if (qty === 0) {
                // First add.
                const variant = pickedVariantId ? enabledVariants.find((v) => v.id === pickedVariantId) : null;
                const item = {
                    product_id: product.id,
                    name: variant ? `${product.name} — ${variant.label}` : product.name,
                    quantity: newQty,
                    selling_price: variant ? variant.selling_price : product.selling_price,
                    image_url: variant?.image_url || (product.images || [])[0],
                    food_type: product.food_type,
                    variant_id: pickedVariantId || undefined,
                };
                try {
                    await api.post(`/cart/items?restaurant_id=${sid}`, item);
                    setQty(newQty);
                    window.dispatchEvent(new Event("cart:update"));
                    toast.success(`Added ${product.name}`);
                } catch (e) {
                    const status = e?.response?.status;
                    const detail = e?.response?.data?.detail;
                    if (status === 409 && detail && typeof detail === "object"
                        && detail.code === "DIFFERENT_RESTAURANT") {
                        const existing = detail.existing_restaurant || "another shop";
                        const ok = window.confirm(
                            `Your cart has items from ${existing}.\n\nWe can only deliver from one shop at a time. Replace your cart with this item?`,
                        );
                        if (!ok) return;
                        await api.post(`/cart/items?restaurant_id=${sid}&force=true`, item);
                        setQty(newQty);
                        window.dispatchEvent(new Event("cart:update"));
                        toast.success(`Cart cleared. Added ${product.name}`);
                        return;
                    }
                    toast.error(typeof detail === "string" ? detail : "Failed to add to cart");
                }
                return;
            }
            // Increment / decrement existing line.
            await api.patch(
                `/cart/items/${product.id}?quantity=${newQty}${pickedVariantId ? `&variant_id=${pickedVariantId}` : ""}`,
            );
            setQty(newQty);
            window.dispatchEvent(new Event("cart:update"));
        } finally {
            setBusy(false);
        }
    };

    const onVariantPicked = async (variant) => {
        setVariantPickerOpen(false);
        setPickedVariantId(variant.id);
        setTimeout(() => post(1), 0);
    };

    // Stop-propagation shell so parent's card click / Link doesn't fire.
    const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

    if (qty === 0) {
        return (
            <>
                <button
                    data-testid={`inline-add-${product.id}`}
                    disabled={busy}
                    onClick={(e) => { stop(e); post(1); }}
                    className={`${boxBase} bg-card text-primary border-primary active:scale-95 disabled:opacity-50`}
                >
                    {busy ? <Loader2 size={iconSize} className="animate-spin" /> : "ADD"}
                </button>
                {variantPickerOpen && (
                    <div onClick={stop}>
                        <ProductVariantPicker
                            product={product}
                            onConfirm={onVariantPicked}
                            onClose={() => setVariantPickerOpen(false)}
                        />
                    </div>
                )}
            </>
        );
    }

    // Qty pill — reuses ``boxBase`` so height + width + border-radius +
    // border-width match the ADD button pixel-for-pixel. Only the fill
    // colour flips (primary bg with white text) to signal "already in
    // cart", and the interior gets three children (-, qty, +).
    return (
        <div
            data-testid={`inline-cart-${product.id}`}
            className={`${boxBase} bg-primary text-primary-foreground border-primary shadow-sm`}
            style={{ justifyContent: "space-between" }}
            onClick={stop}
        >
            <button
                data-testid={`inline-dec-${product.id}`}
                disabled={busy}
                onClick={(e) => { stop(e); post(-1); }}
                className={`h-full ${stepPad} disabled:opacity-50 inline-flex items-center justify-center`}
                aria-label="Decrease quantity"
            >
                <Minus size={iconSize} strokeWidth={3} />
            </button>
            <span
                data-testid={`inline-qty-${product.id}`}
                className={`${textSize} tabular-nums min-w-[16px] text-center`}
            >
                {busy ? <Loader2 size={iconSize} className="animate-spin inline" /> : qty}
            </span>
            <button
                data-testid={`inline-inc-${product.id}`}
                disabled={busy || cantAddMore}
                onClick={(e) => { stop(e); post(1); }}
                className={`h-full ${stepPad} disabled:opacity-40 inline-flex items-center justify-center`}
                aria-label="Increase quantity"
            >
                <Plus size={iconSize} strokeWidth={3} />
            </button>
        </div>
    );
}

/**
 * Reusable low-stock warning that pairs nicely below the ADD control.
 * Exported here (instead of importing StockBadge component tree) so
 * callers can toggle it independently of the inline cart control.
 */
export function LowStockHint({ product }) {
    // iter130 — tenant-configured threshold. Falls back to the default
    // constant when no tenant context is available.
    const threshold = useLowStockThreshold();
    const trackStock = product?.track_stock !== false;
    if (!trackStock || product?.stock == null) return null;
    if (product.stock <= 0 || product.stock > threshold) return null;
    return (
        <span
            data-testid={`inline-low-stock-${product.id}`}
            className="text-[10px] font-bold uppercase tracking-wider text-orange-700"
        >
            Only {product.stock} left
        </span>
    );
}
